<template>
  <ontos-base v-bind="baseProps" ref="base">
    <div v-if="loaded">
      <ontos-page-header :title="$tt('workspaces')">
        <template #controls>
          <ontos-header-action icon="mdi-plus" color="primary" :label="$tt('workspace-create')"
                               :to="{name: 'create-system'}"/>
        </template>
      </ontos-page-header>

      <!-- Os ambientes à esquerda e o que mudou neles à direita; empilhados abaixo do md, com a
           atividade embaixo (escolha do usuário entre cartões ricos, "continuar de onde parou" e
           esta). -->
      <div class="home-columns">
        <section>
          <div v-if="!systems.length" class="text-body-2 text-grey">{{ $tt('workspaces-empty') }}</div>
          <ontos-workspace-row v-for="system of openSystems" :key="system.alias" :system="system"
                               :overview="overviewOf(system)"/>

          <!-- Os encerrados num grupo recolhido no fim, esmaecidos (escolha do usuário, no lugar do
               interruptor "Mostrar encerrados" no cabeçalho). Só existe havendo algum, e lembra se
               ficou aberto. -->
          <template v-if="closedSystems.length">
            <button type="button" class="closed-toggle text-body-2 text-medium-emphasis"
                    :aria-expanded="showClosed" @click="toggleClosed()">
              <v-icon size="small">{{ showClosed ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              {{ $tt('closed-workspaces', {count: closedSystems.length}) }}
            </button>
            <template v-if="showClosed">
              <ontos-workspace-row v-for="system of closedSystems" :key="system.alias" :system="system"
                                   :overview="overviewOf(system)"/>
            </template>
          </template>
        </section>

        <!-- O que mudou por último em todos os ambientes abertos — num encerrado nada muda mais.
             Chega depois da lista, com o resumo, e entra acendendo. -->
        <ontos-section v-if="overviews" class="ontos-arrives" :title="$tt('recent-activity')"
                       :divider="false" :level="2" dense>
          <div v-if="!recent.length" class="text-body-2 text-grey">{{ $tt('recent-activity-empty') }}</div>
          <ontos-recent-row v-for="entry of recent" :key="`${entry.system.alias}.${entry.item.idea}.${entry.item._instance}`"
                            :item="entry.item" :place="`${entry.system.title} › ${entry.item.ideaName || entry.item.idea}`"
                            :to="{name: 'view-instance',
                                  params: {system: entry.system.alias, idea: entry.item.idea, instance: entry.item._instance}}"/>
        </ontos-section>
      </div>
    </div>
  </ontos-base>
</template>

<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import RouteComponent from "@/components/shared/RouteComponent";
import Section from "@/components/shared/Section.vue";
import RecentRow from "@/components/shared/RecentRow.vue";
import WorkspaceRow, {HomeWorkspace} from "@/components/home/WorkspaceRow.vue";
import {RecentItem, WorkspaceOverview} from "@/types/WorkspaceSummary";

/** Quantos alterados a coluna de atividade mostra, de todos os ambientes juntos. */
const RECENT = 15

/**
 * A tela inicial, a porta de entrada do lunedi: os ambientes de quem entra, cada um com o seu nível
 * e os seus números, e o que mudou por último neles (pedido do usuário: era uma grade de cartões
 * azuis com o nome e dois botões).
 *
 * **A lista chega primeiro**, da listagem dos ambientes — o nome, a descrição, o nível —, e os
 * números e a atividade depois, do resumo (`overview`), que é a leitura pesada: a de cada ambiente,
 * com as fórmulas de visibilidade. Sem o resumo (uma falha ao buscá-lo), a lista continua, só sem os
 * números — o mesmo arranjo da página de entrada do ambiente.
 */
@Component({
  components: {
    'ontos-section': Section,
    'ontos-recent-row': RecentRow,
    'ontos-workspace-row': WorkspaceRow
  }
})
class Home extends RouteComponent {
  systems: HomeWorkspace[] = []
  /** O resumo de cada ambiente, ou null enquanto não chegou. */
  overviews: WorkspaceOverview[] | null = null

  /** O grupo dos encerrados aberto — lembrado entre visitas, como era o interruptor. */
  showClosed = false

  /**
   * O resumo, pedido JUNTO com a lista, e esperado só depois de ela aparecer — ver o `afterLoad`. A
   * falha é tratada já no pedido: ela pode chegar enquanto a lista ainda carrega, antes de alguém o
   * esperar, e o navegador a acusaria como promessa rejeitada sem tratamento.
   */
  private overviewRequest: Promise<WorkspaceOverview[] | null> | null = null

  async load() {
    this.showClosed = this.$storage.showClosedWorkspaces()
    //Os dois pedidos saem juntos: um depois do outro, o resumo esperava a lista à toa
    this.overviewRequest = this.$systems.overview().catch(err => {
      console.warn('Não foi possível ler o resumo dos ambientes', err)
      return null
    })
    this.systems = await this.$systems.list() as HomeWorkspace[]
  }

  /** Depois de a lista aparecer: o resumo não a segura. Sem ele, a lista fica sem os números. */
  async afterLoad() {
    this.overviews = await this.overviewRequest
  }

  get openSystems(): HomeWorkspace[] {
    return this.systems.filter(system => !system.closed)
  }

  get closedSystems(): HomeWorkspace[] {
    return this.systems.filter(system => system.closed)
  }

  overviewOf(system: HomeWorkspace): WorkspaceOverview | null {
    return (this.overviews || []).find(overview => overview.system === system.alias) || null
  }

  /**
   * Os alterados de todos os ambientes ABERTOS, do mais novo ao mais velho: num encerrado nada muda
   * mais, e o que mudou nele é passado.
   */
  get recent(): Array<{ system: HomeWorkspace, item: RecentItem }> {
    return this.openSystems
        .flatMap(system => (this.overviewOf(system)?.recent || []).map(item => ({system, item})))
        .sort((a, b) => timeOf(b.item) - timeOf(a.item))
        .slice(0, RECENT)
  }

  toggleClosed() {
    this.showClosed = !this.showClosed
    this.$storage.setShowClosedWorkspaces(this.showClosed)
  }
}

function timeOf(item: RecentItem): number {
  return item._updatedAt ? new Date(item._updatedAt).getTime() : 0
}

export default toNative(Home)
</script>

<style scoped>
/* Empilhadas, com o vão de uma seção; lado a lado do md para cima, a lista mais larga. O vão é o
   `gap`, e não a margem da seção, que na segunda coluna a desalinharia da primeira — o mesmo
   arranjo da página de entrada do ambiente. */
/* A coluna única, empilhada, declarada `minmax(0, 1fr)`: sem coluna declarada a grade cria uma
   `auto`, que mede o conteúdo — e as linhas que cortam com reticências (descrição, coleções) contam
   pela largura do texto inteiro. A coluna crescia até elas, e no telefone a tela inteira ficava mais
   larga que a tela (relato do usuário). */
.home-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 28px;
  column-gap: 48px;
}

.home-columns > .ontos-section {
  margin-top: 0;
}

@media (min-width: 960px) {
  .home-columns {
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    align-items: start;
  }
}

/* A linha que abre o grupo dos encerrados: discreta, como um rótulo, e não um botão de ação */
.closed-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 12px 4px 8px;
  text-align: left;
  cursor: pointer;
}

.closed-toggle:hover {
  color: rgb(var(--v-theme-on-surface));
}
</style>
